import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { FaCalendarAlt, FaFlag, FaGlobe, FaCity, FaMapMarkerAlt, FaStar } from 'react-icons/fa';
import { CHART_COLORS } from '../../../utils/chartColors';
import EventService from '../services/EventService';
import { EventsResponse, UnifiedEvent } from '../types';
import { unifyEvents } from '../services/unifyEvents';
import TenantService from '../../settings/company/services/TenantService';

const TYPE_LABELS: Record<string, string> = {
  public_holiday: 'Feiertag',
  world_day: 'Welttag',
  city_event: 'City Event',
  local_event: 'Lokales Event',
  custom_event: 'Eigenes Event',
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  public_holiday: <FaFlag size={11} />,
  world_day: <FaGlobe size={11} />,
  city_event: <FaCity size={11} />,
  local_event: <FaMapMarkerAlt size={11} />,
  custom_event: <FaStar size={11} />,
};

const HOLIDAY_COLOR = '#16a34a';
const EVENT_COLOR = '#3b82f6';

function formatDateDE(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function formatDateRange(start: string, end: string | null): string {
  if (!end || end === start) return formatDateDE(start);
  return `${formatDateDE(start)} – ${formatDateDE(end)}`;
}

function daysFromNow(dateStr: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T12:00:00');
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86400000);
}

function daysLabel(days: number): string {
  if (days === 0) return 'Heute';
  if (days === 1) return 'Morgen';
  return `in ${days} Tagen`;
}

export default function EventsWidget(): React.ReactElement {
  const [events, setEvents] = useState<UnifiedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    TenantService.getLocations()
      .then(locations => {
        const hq = locations.find((l: any) => l.is_headquarters);
        const locationId = hq?.id || locations[0]?.id;
        if (!locationId) { setLoading(false); return; }

        const today = new Date().toISOString().split('T')[0];
        return EventService.getEvents(locationId, today, 40);
      })
      .then(data => {
        if (data) setEvents(unifyEvents(data));
      })
      .catch(() => { setError(true); })
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, UnifiedEvent[]>();
    for (const ev of events) {
      const list = map.get(ev.date) || [];
      list.push(ev);
      map.set(ev.date, list);
    }
    return Array.from(map.entries())
      .filter(([date]) => daysFromNow(date) >= 0)
      .slice(0, 15);
  }, [events]);

  if (loading) {
    return (
      <div className="glass-card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <FaCalendarAlt size={20} color={CHART_COLORS.navy} />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: CHART_COLORS.navy }}>Feiertage & Events</h2>
        </div>
        <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>Laden...</div>
      </div>
    );
  }

  return (
    <div className="glass-card" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FaCalendarAlt size={20} color={CHART_COLORS.navy} />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: CHART_COLORS.navy }}>Feiertage & Events</h2>
        </div>
        <span style={{ fontSize: 12, color: '#888' }}>Nächste 40 Tage</span>
      </div>

      {error ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>
          Events konnten nicht geladen werden.
        </div>
      ) : grouped.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>
          Keine Events in den nächsten 40 Tagen
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 360, overflowY: 'auto' }}>
          {grouped.map(([date, dayEvents]) => {
            const days = daysFromNow(date);
            return (
              <div key={date} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: CHART_COLORS.navy }}>
                    {formatDateDE(date)}
                  </span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{daysLabel(days)}</span>
                </div>
                {dayEvents.map((ev, i) => {
                  const color = ev.isHoliday ? HOLIDAY_COLOR : EVENT_COLOR;
                  return (
                    <div
                      key={`${ev.type}-${ev.name}-${i}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 8px',
                        marginLeft: 4,
                        borderRadius: 4,
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ color, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                        {TYPE_ICONS[ev.type]}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: CHART_COLORS.navy, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.name}
                      </span>
                      {ev.endDate && ev.endDate !== ev.date && (
                        <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
                          bis {formatDateDE(ev.endDate)}
                        </span>
                      )}
                      {ev.location && (
                        <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{ev.location}</span>
                      )}
                      <span
                        style={{
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 8,
                          background: ev.isHoliday ? '#dcfce7' : '#dbeafe',
                          color: ev.isHoliday ? '#166534' : '#1e40af',
                          fontWeight: 500,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {TYPE_LABELS[ev.type]}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
